import { proStudioUrqlGql as urqlGql } from '@pypestream/api-services';

import { Pype } from '../xstate/prostudio.api-service.types';
import { checkForErrors } from './check-for-errors';
import { getChannelsFromPypes } from './get-channels-from-pypes';
import {
  toPlatformChannel,
  type PlatformChannel,
} from './map-connection-to-row';

type Env = 'live' | 'sandbox';

/**
 * One Reporting API credential row. API keys are scoped to a pype, so a
 * credential's channels are that pype's channels. `id` is the key string —
 * ApiKeyDetail exposes no separate id, so the key doubles as the identifier
 * for the by-id update/delete endpoints.
 */
export interface ReportingApiCredentialRow {
  id: string;
  pypeId: string;
  pypeName: string;
  name: string;
  description: string;
  key: string;
  channels: PlatformChannel[];
  env: Env;
}

/**
 * Keys for ONE pype in ONE env — a single GraphQL request.
 *
 * Exported for the Channel Settings load (PSTUDIO-4253): that page renders only
 * the open channel's own credentials, so it asks for exactly those instead of
 * going through `getPypeKeys`, which walks every pype in the project.
 */
export const getPypeKeysForPype = async ({
  pype,
  projectId,
  customerId,
  env,
}: {
  pype: Pype;
  projectId: string;
  customerId: string;
  env: Env;
}): Promise<ReportingApiCredentialRow[]> => {
  const pypeId = pype.id;
  if (!pypeId) return [];

  const fetchKeys = {
    live: async () => {
      const response = await urqlGql.getPypeKeysLive({
        projectId,
        customerId,
        pypeId,
      });
      checkForErrors(response);
      return response.data?.pypesGetCustomersPypesKeys?.keys;
    },
    sandbox: async () => {
      const response = await urqlGql.getPypeKeysSandbox({
        projectId,
        customerId,
        pypeId,
      });
      checkForErrors(response);
      return response.data?.pypesGetCustomersPypesKeysSandbox?.keys;
    },
  }[env];

  const keys = (await fetchKeys()) ?? [];

  const channels = getChannelsFromPypes([pype]).map(toPlatformChannel);

  return keys
    .filter((key): key is NonNullable<typeof key> => !!key?.key)
    .map((key) => ({
      id: key.key ?? '',
      pypeId: pype.id ?? '',
      pypeName: pype.name ?? '',
      name: key.friendly_name ?? '',
      description: key.description ?? '',
      key: key.key ?? '',
      channels,
      env,
    }));
};

/**
 * Fetches every API key across the given project pypes (one request per pype)
 * and flattens them into credential rows. A pype whose key request fails is
 * skipped rather than failing the whole list.
 */
export const getPypeKeys = async ({
  pypes,
  projectId,
  customerId,
  env,
}: {
  pypes: Pype[];
  projectId: string;
  customerId: string;
  env: Env;
}): Promise<ReportingApiCredentialRow[]> => {
  const results = await Promise.all(
    pypes.map((pype) =>
      getPypeKeysForPype({ pype, projectId, customerId, env }).catch(() => [])
    )
  );

  return results.flat();
};
